import { FC, useEffect, useMemo, useRef, useState } from "react";
import { usePlatformFeatureFlag } from "@datassential/platform-ui-lib";
import Page from "@containers/Page/Page";
import PageContent from "@containers/PageContent/PageContent";
import {
    ChainDetailsDataContext,
    ChainPageScrollId,
    filterSectionsForVisibility,
    getAvailableSections,
} from "./ChainPage.helpers";
import ChainsDetailsHeader from "./ChainsDetailsHeader/ChainsDetailsHeader";
import { useHistory } from "react-router-dom";
import useChainDetails from "@core/apiHooks/useChainDetails";
import useQueryParam from "@core/hooks/useQueryParam";
import "./ChainPage.styles.scss";
import "./ChainPageScroll.styles.scss";
import { routeNames } from "@core/routeNames";
import { getSectionId } from "@helpers";
import GapAnalysisModal from "@pages/_shared/GapAnalysisModal/GapAnalysisModal";
import Loader from "@components/new/Loader/Loader";
import { CustomBaseScrollbars } from "@components/CustomScrollbarsVirtualList/CustomScrollbarsVirtualList";
import { PermissionLevelEnum } from "@core/models/PermissionSection";
import ChainPermissionPopup from "@components/ChainPermissionPopup/ChainPermissionPopup";
import UsChainsHeader from "@pages/_shared/UsChainsHeader/UsChainsHeader";
import useFireflyChains from "@apiHooks/useFireflyChains";
import { useTabsAutoScroll } from "@core/hooks/useTabsAutoScroll";
import useSocialLinks from "@apiHooks/useSocialLinks";
import { useChainSocialData } from "@apiHooks/useChainSocialData";

const ChainPage: FC = () => {
    const history = useHistory();
    const chainId = useQueryParam("id");
    const menuIncludesParam = useQueryParam("menuIncludes");
    const menuIncludes = useMemo(
        () => (menuIncludesParam ? menuIncludesParam.split(',').map(t => t.trim()).filter(Boolean) : []),
        [menuIncludesParam]
    );
    const { isLoading: isAllChainsLoading, data: allChainsData } = useFireflyChains();
    const {
        data: {
            profileData: {
                data,
                permissionLevel
            },
            isInTop1000,
        },
        isLoading,
        downloadReports,
    } = useChainDetails(chainId);

    const { data: socialLinksData, isLoading: isLoadingSocialLinks } = useSocialLinks(data.mainData.socialLinks);
    const { data: chainSocialData, isLoading: isLoadingChainSocial } = useChainSocialData(data.mainData.id || null);

    const scrollContainerRef = useRef<any>(null);
    const [isGapReportPopupOpened, setIsGapReportPopupOpened] = useState(false);

    useEffect(() => {
        if (!chainId) {
            history.push(routeNames.topChains);
        }
    }, [chainId, history]);

    const handleScrollbarsMount = (scrollbars: any) => {
        if (scrollbars) {
            const viewElm = scrollbars.view;
            scrollContainerRef.current = viewElm;
        }
    };

    const isSocialsLoading = useMemo(() => isLoadingSocialLinks || isLoadingChainSocial, [isLoadingSocialLinks, isLoadingChainSocial]);
    const socialsHasData = useMemo(() => {
        return socialLinksData.twitter.length > 0 || socialLinksData.youtube.length > 0 || chainSocialData.hasData;
    }, [socialLinksData, chainSocialData]);
    const isSsbEnabled = usePlatformFeatureFlag('SNAP SSB on Chains');

    const availableSections = useMemo(
        () => filterSectionsForVisibility(
            getAvailableSections(data.sectionsData),
            { isSocialsLoading, socialsHasData, isSsbEnabled }
        ),
        [data.sectionsData, isSocialsLoading, socialsHasData, isSsbEnabled]
    );

    useTabsAutoScroll(isLoading, availableSections, 'TabList__link');

    return (
        <ChainDetailsDataContext.Provider value={{
            data,
            sections: availableSections,
            openGapReportPopup: () => setIsGapReportPopupOpened(true),
            downloadReports: downloadReports,
            socialLinksData,
            chainSocialData,
            isLoadingSocialLinks,
            isLoadingChainSocial,
            menuIncludes,
        }}>
        {/* @ts-ignore */}
        <Page>
            <PageContent>
                <CustomBaseScrollbars
                    refSetter={handleScrollbarsMount}
                    id={ChainPageScrollId}
                >
                    <UsChainsHeader
                        style={{ top: 0, position: "sticky" }}
                        data={allChainsData}
                        isLoading={isAllChainsLoading || isLoading}
                    />
                    <div className="ChainPage">
                        {isLoading ? (
                            <div className="ChainPage__loader">
                                <Loader className="is-no-bg"/>
                            </div>
                        ) : (
                            <>
                                {permissionLevel === PermissionLevelEnum.Full && (
                                    <>
                                        <div className="ChainPage__spacer"/>
                                        <div className="ChainPage__header">
                                            <ChainsDetailsHeader
                                                ref={scrollContainerRef}
                                            />
                                        </div>
                                        <div className="ChainPage__content">
                                            {availableSections.map((section) => (
                                                <div
                                                    key={section.id}
                                                    className="ChainPage__section"
                                                    id={getSectionId(section.id)}
                                                >
                                                    {section.content}
                                                </div>
                                            ))}
                                        </div>
                                        {isGapReportPopupOpened && data.mainData.id != null && (
                                            <GapAnalysisModal
                                                close={() => setIsGapReportPopupOpened(false)}
                                                chainId={data.mainData.id}
                                                chainName={data.mainData.name ?? ''}
                                            />
                                        )}
                                    </>
                                )}

                                <ChainPermissionPopup isOpen={!isInTop1000 && permissionLevel === PermissionLevelEnum.None} />
                            </>
                        )}
                    </div>
                </CustomBaseScrollbars>
            </PageContent>
        </Page>
        </ChainDetailsDataContext.Provider>
    );
};

export default ChainPage;
